import { Helmet } from 'react-helmet-async';
import { PublicNavbar } from '@/components/layout/PublicNavbar';
import { PublicFooter } from '@/components/layout/PublicFooter';
import { PageHero, SectionHeading } from '@/components/layout/PublicSections';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Franchise, Branch } from '@/types';
import {
  Phone, Mail, MapPin, Building2, Loader2, Navigation, ExternalLink,
  Instagram, Facebook, Twitter,
} from 'lucide-react';

// Anchor styled as an outline button (Button renders a <button>, which can't
// host an href). Matches the outline variant in Button.tsx.
const outlineLink =
  'mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg border border-hairline px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:border-hairline-strong hover:bg-surface-2';

export default function Contact() {
  const { t } = useTranslation();
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loadingFranchises, setLoadingFranchises] = useState(true);
  const [loadingBranches, setLoadingBranches] = useState(true);

  useEffect(() => {
    const fetchFranchises = async () => {
      try {
        const franchisesQuery = query(
          collection(db, 'franchises'),
          where('isActive', '==', true)
        );
        const querySnapshot = await getDocs(franchisesQuery);
        const franchisesData = querySnapshot.docs.map(doc => ({
          franchiseId: doc.id,
          ...doc.data()
        } as Franchise));

        setFranchises(franchisesData);
      } catch (error) {
        console.error('Error fetching franchises:', error);
      } finally {
        setLoadingFranchises(false);
      }
    };

    const fetchBranches = async () => {
      try {
        const branchesQuery = query(
          collection(db, 'branches'),
          where('isActive', '==', true)
        );
        const querySnapshot = await getDocs(branchesQuery);
        const branchesData = querySnapshot.docs.map(doc => ({
          branchId: doc.id,
          ...doc.data()
        } as Branch));

        setBranches(branchesData);
      } catch (error) {
        console.error('Error fetching branches:', error);
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchFranchises();
    fetchBranches();
  }, []);

  const getBranchCount = (franchiseId: string) => {
    return branches.filter(b => b.franchiseId === franchiseId).length;
  };

  const getMapUrl = (branch: Branch) => {
    const address = `${branch.address}, ${branch.city}, ${branch.province}, ${branch.country}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const socials = [
    { Icon: Instagram, label: 'Instagram', href: 'https://instagram.com/monkeybarberclub' },
    { Icon: Facebook, label: 'Facebook', href: 'https://facebook.com/monkeybarberclub' },
    { Icon: Twitter, label: 'Twitter', href: 'https://twitter.com/monkeybarberclub' },
  ];

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <title>{t('contact.seo.title')}</title>
      </Helmet>
      <PublicNavbar />

      <PageHero
        title={t('contact.hero.title')}
        subtitle={t('contact.hero.subtitle')}
      />

      {/* Contact info */}
      <section className="border-b border-hairline bg-surface py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6">
          <SectionHeading title={t('contact.info.title')} />

          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-4 rounded-xl border border-hairline bg-canvas p-6">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-hairline bg-surface-2 text-ink-subtle">
                <Phone className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-sans text-base font-semibold tracking-tight text-ink">{t('contact.info.phone')}</h3>
                <a href="tel:+34604216330" className="mt-1 inline-block text-ink-muted transition-colors hover:text-accent">
                  +34 604 216 330
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-xl border border-hairline bg-canvas p-6">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-hairline bg-surface-2 text-ink-subtle">
                <Mail className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h3 className="font-sans text-base font-semibold tracking-tight text-ink">{t('contact.info.email')}</h3>
                <a href="mailto:monkeybarbershop2@gmail.com" className="mt-1 inline-block break-all text-ink-muted transition-colors hover:text-accent">
                  monkeybarbershop2@gmail.com
                </a>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-hairline bg-canvas p-6">
            <h3 className="font-sans text-base font-semibold tracking-tight text-ink">{t('contact.info.social')}</h3>
            <div className="mt-4 flex flex-wrap gap-3">
              {socials.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-hairline px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:border-hairline-strong hover:text-ink"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="border-b border-hairline bg-canvas py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeading
            eyebrow={t('contact.locations.subtitle')}
            title={t('contact.locations.title')}
          />

          {loadingBranches ? (
            <div className="flex flex-col items-center py-16 text-ink-subtle">
              <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
              <p className="mt-4">{t('contact.locations.loading')}</p>
            </div>
          ) : branches.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-ink-subtle">
              <MapPin className="h-8 w-8" aria-hidden="true" />
              <p className="mt-4">{t('contact.locations.empty')}</p>
            </div>
          ) : (
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {branches.map((branch) => (
                <div
                  key={branch.branchId}
                  className="flex flex-col overflow-hidden rounded-xl border border-hairline bg-surface transition-colors hover:border-hairline-strong"
                >
                  {branch.photo && (
                    <div className="h-40 overflow-hidden border-b border-hairline">
                      <img src={branch.photo} alt={branch.name} className="h-full w-full object-cover" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-sans text-lg font-semibold tracking-tight text-ink">{branch.name}</h3>
                    <div className="mt-3 space-y-2 text-sm text-ink-muted">
                      <div className="flex items-start gap-2">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                        <span>{branch.address}, {branch.city}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                        <a href={`tel:${branch.phone}`} className="transition-colors hover:text-accent">{branch.phone}</a>
                      </div>
                      {branch.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                          <a href={`mailto:${branch.email}`} className="break-all transition-colors hover:text-accent">{branch.email}</a>
                        </div>
                      )}
                    </div>
                    <a href={getMapUrl(branch)} target="_blank" rel="noopener noreferrer" className={outlineLink}>
                      <Navigation className="h-4 w-4" aria-hidden="true" />
                      {t('contact.locations.getDirections')}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Franchises */}
      <section className="bg-surface py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeading
            eyebrow={t('contact.franchises.subtitle')}
            title={t('contact.franchises.title')}
          />

          {loadingFranchises ? (
            <div className="flex flex-col items-center py-16 text-ink-subtle">
              <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
              <p className="mt-4">{t('contact.franchises.loading')}</p>
            </div>
          ) : franchises.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-ink-subtle">
              <Building2 className="h-8 w-8" aria-hidden="true" />
              <p className="mt-4">{t('contact.franchises.empty')}</p>
            </div>
          ) : (
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {franchises.map((franchise) => {
                const branchCount = getBranchCount(franchise.franchiseId);
                return (
                  <div
                    key={franchise.franchiseId}
                    className="flex flex-col rounded-xl border border-hairline bg-canvas p-6 transition-colors hover:border-hairline-strong"
                  >
                    {franchise.logo && (
                      <img src={franchise.logo} alt={franchise.name} className="mb-4 h-16 w-auto object-contain" />
                    )}
                    <h3 className="font-sans text-lg font-semibold tracking-tight text-ink">{franchise.name}</h3>
                    {franchise.description && (
                      <p className="mt-2 line-clamp-2 text-sm text-ink-muted">{franchise.description}</p>
                    )}
                    <div className="mt-4 space-y-2 text-sm text-ink-muted">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                        <span>
                          {branchCount} {branchCount === 1 ? t('contact.franchises.branches') : t('contact.franchises.branches_plural')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                        <a href={`tel:${franchise.phone}`} className="transition-colors hover:text-accent">{franchise.phone}</a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                        <a href={`mailto:${franchise.email}`} className="break-all transition-colors hover:text-accent">{franchise.email}</a>
                      </div>
                    </div>
                    {franchise.website && (
                      <a href={franchise.website} target="_blank" rel="noopener noreferrer" className={outlineLink}>
                        <ExternalLink className="h-4 w-4" aria-hidden="true" />
                        {t('contact.franchises.viewWebsite')}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
